import { HttpClient, HttpHeaders } from '@angular/common/http';

import Compressor from 'compressorjs';
import { Injectable } from '@angular/core';
import { blobToDataURL } from 'blob-util';
import { environment } from '../../environments/environment';
import { firstValueFrom } from 'rxjs';
import heic2Any from 'heic2any';

export type ImageUploadType = 'maps';
export type ImageFileType = 'uncropped' | 'cropped' | 'series';
export type ImagePayload = {
  id: number;
  image: URL;
  width_px: number;
  height_px: number;
};
type ImagePayloadResponse = {
  id: number;
  image: URL;
  width: number;
  height: number;
};

async function compress(image, options): Promise<Blob> {
  return new Promise((resolve, reject) => {
    new Compressor(image, {
      success: resolve,
      error: reject,
      ...options,
    });
  });
}

// convert a File to a Blob
const fileToBlob = (file: File): Blob => {
  return new Blob([file], { type: file.type });
};

async function heicToBlob(file: File, max = 1000): Promise<Blob> {
  const blob = fileToBlob(file);
  const pngBlob = (await heic2Any({
    blob,
    toType: 'image/png',
  })) as Blob;
  return await compress(pngBlob, {
    maxHeight: max,
    maxWidth: max,
  });
}

@Injectable({
  providedIn: 'root',
})
export class ImageService {
  constructor(
    // private camera: CameraSource,
    // private file: File,
    private http: HttpClient,
  ) {}

  async getBlobFromFile(file: File): Promise<Blob> {
    try {
      return await compress(file, {
        maxHeight: 1000,
        maxWidth: 1000,
      });
    } catch (e) {
      return await heicToBlob(file, 1000);
    }
  }

  async getThumbnailFromFile(file: File): Promise<Blob> {
    try {
      return await compress(file, {
        maxHeight: 500,
        maxWidth: 500,
      });
    } catch (e) {
      return await heicToBlob(file, 500);
    }
  }

  async getThumbnailFromBlob(blob: Blob): Promise<Blob> {
    return await compress(blob, {
      maxHeight: 500,
      maxWidth: 500,
    });
  }

  // public dataURLToBlob(dataURL): Promise<Blob> {
  // fetch not supported on iOS 9.3 Safari
  //   return fetch(dataURL).then(res => res.blob());
  // }

  public dataURLtoBlob(dataURL: string): Blob {
    const arr = dataURL.split(','),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  }

  // private _arrayBufferToBlob(imageData): Blob {
  //   const data = new Buffer(imageData);
  //   const fileType = FileType(data);
  //   const mime = (fileType && fileType.mime) || 'image/jpeg';
  //   return new Blob([data], { type: mime });
  // }

  public blobToDataURL(blob: Blob): Promise<string> {
    return blobToDataURL(blob);
  }

  /**
   * Upload Data URI/URL to API server and get back a cloudinary
   * https URL and image dimensions
   *
   */
  public async uploadDataURL(
    dataURL: string,
    userId: string,
    contentType: ImageUploadType,
    contentId: string,
    imageCategory: ImageFileType,
  ): Promise<ImagePayload> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    return firstValueFrom(
      this.http.put<ImagePayloadResponse>(
        `${environment.acmAPI}images/${userId}/${contentType}/${contentId}/${imageCategory}/`,
        {
          content_type: contentType,
          content_id: contentId,
          data_url: dataURL,
          image_category: imageCategory,
        },
        httpOptions,
      ),
    ).then(({ id, image, width, height }) => {
      return { id, image, width_px: width, height_px: height };
    });
  }

  public async fetchImage(url: string): Promise<Blob> {
    return firstValueFrom(this.http.get(url, { responseType: 'blob' }));
  }
}
